import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

// Generic search-as-you-type entity picker. Used by CustomerPicker
// and StorePicker; designed for reuse by future "pick an X from N"
// flows (assignees, robots, etc.).
//
// Contract:
//   - Caller owns the `value` (currently selected entity, or null).
//   - Caller provides the data source via `fetchOptions(query)`.
//   - This component handles: input + debounce + react-query
//     fetching + a dropdown of results + clear + outside-click
//     close + ESC close.
//   - When the user picks a row, we call `onChange(option)`. When
//     they click X (or empty the input + blur), we call
//     `onChange(null)`. The caller's mutation/form decides what to
//     do with that signal.
//
// We deliberately do NOT auto-fetch on mount when the input is
// empty unless `autoOpenOnFocus` is true — the empty-string case
// can fan out to thousands of rows on busy tenants and the user
// usually knows roughly what they're looking for.

interface EntityPickerProps<T> {
  value: T | null;
  onChange: (option: T | null) => void;

  /**
   * Data source. Caller wires this to a paginated list API; the
   * picker will pass the current search query (1-N characters).
   * Return a thin slice (e.g. 20 rows max) — the dropdown isn't
   * virtualized.
   */
  fetchOptions: (query: string) => Promise<T[]>;

  /** react-query cache key prefix — must be unique per entity type. */
  queryKey: string;

  /** Stable id extractor. */
  getId: (option: T) => string;

  /** Primary label rendered in the input + the option row. */
  getLabel: (option: T) => string;

  /** Optional secondary label rendered under the primary in the option row. */
  getSubLabel?: (option: T) => string;

  /** Minimum characters before fetchOptions is called. Default 1. */
  minChars?: number;

  /** Debounce delay in ms before firing a fetchOptions call. Default 250. */
  debounceMs?: number;

  /** Whether the picker is disabled (no input, no dropdown). */
  disabled?: boolean;

  /** Placeholder for the empty input. */
  placeholder?: string;

  /** Show the X clear button when a value is selected. Default true. */
  clearable?: boolean;

  /** Empty-state caption when fetchOptions returns []. */
  emptyMessage?: ReactNode;

  /** Caller-supplied testid prefix. Each subpart gets a suffix. */
  'data-testid'?: string;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 34,
  padding: '0 32px 0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
};

export function EntityPicker<T>({
  value,
  onChange,
  fetchOptions,
  queryKey,
  getId,
  getLabel,
  getSubLabel,
  minChars = 1,
  debounceMs = 250,
  disabled = false,
  placeholder,
  clearable = true,
  emptyMessage = '결과 없음',
  'data-testid': testId,
}: EntityPickerProps<T>) {
  const [inputValue, setInputValue] = useState<string>(value ? getLabel(value) : '');
  const [open, setOpen] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);
  const reactId = useId();

  // Sync input value when the caller changes `value` externally
  // (e.g. reset on modal close). We intentionally don't sync the
  // other direction — letting the input out-of-sync with `value`
  // is what enables type-to-search.
  useEffect(() => {
    if (value === null) {
      setInputValue('');
    } else {
      setInputValue(getLabel(value));
    }
  }, [value, getLabel]);

  // Debounce the input value into the react-query queryKey.
  useEffect(() => {
    const trimmed = inputValue.trim();
    if (trimmed.length < minChars) {
      setDebouncedQuery('');
      return;
    }
    const t = window.setTimeout(() => setDebouncedQuery(trimmed), debounceMs);
    return () => window.clearTimeout(t);
  }, [inputValue, minChars, debounceMs]);

  const optionsQ = useQuery({
    queryKey: [queryKey, debouncedQuery],
    queryFn: () => fetchOptions(debouncedQuery),
    enabled: debouncedQuery.length >= minChars,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    window.addEventListener('mousedown', onDocClick);
    return () => window.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handlePick = useCallback(
    (option: T) => {
      onChange(option);
      setInputValue(getLabel(option));
      setOpen(false);
    },
    [onChange, getLabel],
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setInputValue('');
    setOpen(false);
  }, [onChange]);

  const options = optionsQ.data ?? [];
  const showDropdown = open && inputValue.trim().length >= minChars;

  const stableTestId = useMemo(() => testId ?? `picker-${reactId}`, [testId, reactId]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }} data-testid={stableTestId}>
      <input
        type="text"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if (inputValue.trim().length >= minChars) setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        placeholder={placeholder}
        disabled={disabled}
        style={inputStyle}
        data-testid={`${stableTestId}-input`}
      />
      {clearable && value !== null && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="선택 해제"
          style={{
            position: 'absolute',
            right: 6,
            top: 4,
            border: 'none',
            background: 'transparent',
            fontSize: 16,
            color: 'var(--fg-muted)',
            cursor: 'pointer',
            padding: '2px 6px',
          }}
          data-testid={`${stableTestId}-clear`}
        >
          ×
        </button>
      )}
      {showDropdown && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 2,
            maxHeight: 240,
            overflowY: 'auto',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-md)',
            zIndex: 10,
          }}
          data-testid={`${stableTestId}-dropdown`}
        >
          {optionsQ.isFetching && (
            <div
              style={{ padding: '8px 10px', fontSize: 12, color: 'var(--fg-muted)' }}
              data-testid={`${stableTestId}-loading`}
            >
              검색 중…
            </div>
          )}
          {!optionsQ.isFetching && options.length === 0 && (
            <div
              style={{ padding: '8px 10px', fontSize: 12, color: 'var(--fg-muted)' }}
              data-testid={`${stableTestId}-empty`}
            >
              {emptyMessage}
            </div>
          )}
          {options.map((option) => (
            <button
              type="button"
              key={getId(option)}
              onClick={() => handlePick(option)}
              role="option"
              aria-selected={value !== null && getId(value) === getId(option)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '6px 10px',
                background:
                  value !== null && getId(value) === getId(option)
                    ? 'var(--bg-muted)'
                    : 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--border-subtle)',
                cursor: 'pointer',
                fontSize: 13,
              }}
              data-testid={`${stableTestId}-option-${getId(option)}`}
            >
              <div>{getLabel(option)}</div>
              {getSubLabel && (
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>
                  {getSubLabel(option)}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
